import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Field } from "@/components/kit";
import { Brand } from "@/components/Brand";
import { useBranding } from "@/lib/branding";

function Frame({ title, subtitle, children }) {
  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="flex flex-col px-6 py-8 sm:px-12">
        <Link to="/" className="flex items-center gap-2"><Brand /></Link>
        <div className="my-auto w-full max-w-md py-12 ka-fade">
          <h1 className="text-4xl font-extrabold text-slate-950">{title}</h1>
          <p className="mt-2 text-slate-600">{subtitle}</p>
          <div className="mt-8">{children}</div>
        </div>
      </div>
      <div className="relative hidden lg:block">
        <img src="/img/hero.jpg" alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-slate-950/40" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="font-mono text-xs uppercase tracking-widest text-emerald-300">Food safety diary</div>
          <p className="mt-3 font-display text-3xl font-extrabold leading-tight">Every check, every reading, every sign-off — timestamped and inspection-ready.</p>
        </div>
      </div>
    </div>
  );
}

function useSubmit(fn) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return { error, busy, run };
}

const Submit = ({ busy, children, testid }) => (
  <button data-testid={testid} disabled={busy} className="ka-press flex w-full items-center justify-center gap-2 rounded-full bg-emerald-600 px-6 py-3.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">
    {busy ? "Please wait…" : children} {!busy && <ArrowRight className="h-4 w-4" />}
  </button>
);

const Err = ({ error }) => error ? <div data-testid="auth-error" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div> : null;

export function Login() {
  const { applySession } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next");
  const [f, setF] = useState({ email: "", password: "" });
  const { error, busy, run } = useSubmit(async () => {
    const { data } = await api.post("/auth/login", f);
    await applySession(data);
    nav(next && next.startsWith("/app") ? next : "/app");
  });
  return (
    <Frame title="Welcome back" subtitle="Sign in to your food safety diary.">
      <form onSubmit={run} className="space-y-5">
        <Field label="Email"><input data-testid="login-email-input" type="email" required className="ka-input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
        <Field label="Password"><input data-testid="login-password-input" type="password" required className="ka-input" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></Field>
        <div className="-mt-2 text-right"><Link data-testid="forgot-password-link" to="/forgot-password" className="text-sm font-semibold text-emerald-700 hover:underline">Forgot password?</Link></div>
        <Err error={error} />
        <Submit busy={busy} testid="login-submit-button">Sign in</Submit>
      </form>
      <p className="mt-6 text-sm text-slate-600">New here? <Link data-testid="goto-signup-link" to="/signup" className="font-semibold text-emerald-700 hover:underline">Create your business account</Link></p>
      <p className="mt-2 text-sm text-slate-500">Kitchen staff: open your business's staff app link and use your PIN.</p>
    </Frame>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const { error, busy, run } = useSubmit(async () => {
    await api.post("/auth/forgot-password", { email });
    setSent(true);
  });
  return (
    <Frame title="Forgot your password?" subtitle="Enter your email and we'll send you a link to choose a new one.">
      {sent ? (
        <div data-testid="forgot-sent-message" className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">If an account exists for <b>{email}</b>, a reset link is on its way. It works once, for 1 hour. Check your spam folder too.</div>
      ) : (
        <form onSubmit={run} className="space-y-5">
          <Field label="Email"><input data-testid="forgot-email-input" type="email" required className="ka-input" value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
          <Err error={error} />
          <Submit busy={busy} testid="forgot-submit-button">Send reset link</Submit>
        </form>
      )}
      <p className="mt-6 text-sm text-slate-600"><Link data-testid="back-to-login-link" to="/login" className="font-semibold text-emerald-700 hover:underline">Back to sign in</Link></p>
      <p className="mt-2 text-sm text-slate-500">Kitchen staff using a PIN: ask your manager to reset it.</p>
    </Frame>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const invite = params.get("invite") === "1";
  const [f, setF] = useState({ password: "", confirm: "" });
  const [done, setDone] = useState(false);
  const { error, busy, run } = useSubmit(async () => {
    if (f.password !== f.confirm) throw new Error("The passwords don't match");
    await api.post("/auth/reset-password", { token, password: f.password });
    setDone(true);
  });
  return (
    <Frame title={invite ? "Welcome! Choose your password" : "Choose a new password"} subtitle={invite ? "Set a password to finish setting up your KitchenAudit.app account." : "You'll be signed out on other devices once it's changed."}>
      {!token ? <div data-testid="reset-invalid-message" className="text-sm text-rose-700">This link is missing its reset code. Please request a new one.</div>
        : done ? (
          <div data-testid="reset-done-message" className="space-y-4">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">{invite ? "You're all set. Sign in with your email and new password." : "Password changed. You can now sign in with your new password."}</div>
            <Link data-testid="reset-goto-login" to="/login" className="ka-press inline-flex rounded-full bg-emerald-600 px-6 py-3 text-sm font-semibold text-white hover:bg-emerald-700">Sign in</Link>
          </div>
        ) : (
          <form onSubmit={run} className="space-y-5">
            <Field label="New password" hint="At least 8 characters"><input data-testid="reset-password-input" type="password" minLength={8} required className="ka-input" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></Field>
            <Field label="Confirm new password"><input data-testid="reset-confirm-input" type="password" minLength={8} required className="ka-input" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} /></Field>
            <Err error={error} />
            <Submit busy={busy} testid="reset-submit-button">{invite ? "Set password" : "Change password"}</Submit>
          </form>
        )}
      <p className="mt-6 text-sm text-slate-600"><Link to="/forgot-password" data-testid="reset-request-new-link" className="font-semibold text-emerald-700 hover:underline">Request a new link</Link></p>
    </Frame>
  );
}

function SignupCode({ code, setCode }) {
  const [open, setOpen] = useState(!!code);
  const [res, setRes] = useState(null);
  const check = async () => {
    if (!code.trim()) return;
    try {
      const { data } = await api.post("/public/coupon/check", { code });
      setRes({ ok: true, text: `${data.code} · ${data.summary}${data.kind === "free" ? ". Starts as soon as you create your account." : ". Applied when you first pay."}` });
    } catch (err) {
      setRes({ ok: false, text: errMsg(err) });
    }
  };
  useEffect(() => {
    if (code) check();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!open) return <button type="button" data-testid="signup-code-toggle" onClick={() => setOpen(true)} className="text-sm font-semibold text-emerald-700 hover:underline">Have a coupon code?</button>;
  return (
    <Field label="Coupon code (optional)">
      <div className="flex gap-2">
        <input data-testid="signup-code-input" maxLength={30} className="ka-input flex-1 font-mono uppercase" value={code} onChange={(e) => { setCode(e.target.value); setRes(null); }} onBlur={check} />
        <button type="button" data-testid="signup-code-check" onClick={check} className="ka-press rounded-full border border-slate-300 px-4 text-sm font-semibold hover:border-slate-900">Check</button>
      </div>
      {res && <p data-testid="signup-code-result" className={`mt-1.5 text-xs ${res.ok ? "text-emerald-700" : "text-rose-600"}`}>{res.text}</p>}
    </Field>
  );
}

export function Signup() {
  const { applySession } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { trial_days } = useBranding();
  const [f, setF] = useState({ business_name: (params.get("business") || "").slice(0, 80), venue_name: (params.get("venue") || "").slice(0, 80), name: "", email: "", password: "" });
  const [code, setCode] = useState((params.get("code") || "").slice(0, 30));
  const urlCode = (params.get("code") || "").slice(0, 30);
  const src = ["qr", "link"].includes(params.get("src")) ? params.get("src") : null;
  useEffect(() => {
    if (urlCode && src) api.post("/public/coupon/visit", { code: urlCode, src }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const { error, busy, run } = useSubmit(async () => {
    const body = { ...f, ...(params.get("from") === "demo" ? { source: "demo" } : {}), ...(code.trim() ? { coupon_code: code.trim(), ...(src && code.trim().toUpperCase() === urlCode.trim().toUpperCase() ? { coupon_src: src } : {}) } : {}) };
    const { data } = await api.post("/auth/register", body);
    await applySession(data);
    if (code.trim()) toast.success(data.business?.payment_method === "free_code" ? "Code applied: your free access has started" : "Code saved: it's taken off when you first pay");
    nav("/app");
  });
  return (
    <Frame title="Start your free trial" subtitle={`${trial_days} days free. Your staff app is ready the moment you finish.`}>
      <form onSubmit={run} className="space-y-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Business name"><input data-testid="signup-business-input" required className="ka-input" value={f.business_name} onChange={set("business_name")} placeholder="e.g. The Red Lion Group" /></Field>
          <Field label="First venue"><input data-testid="signup-venue-input" required className="ka-input" value={f.venue_name} onChange={set("venue_name")} placeholder="e.g. High Street kitchen" /></Field>
        </div>
        <Field label="Your name"><input data-testid="signup-name-input" required className="ka-input" value={f.name} onChange={set("name")} /></Field>
        <Field label="Email"><input data-testid="signup-email-input" type="email" required className="ka-input" value={f.email} onChange={set("email")} /></Field>
        <Field label="Password" hint="At least 8 characters"><input data-testid="signup-password-input" type="password" minLength={8} required className="ka-input" value={f.password} onChange={set("password")} /></Field>
        <SignupCode code={code} setCode={setCode} />
        <Err error={error} />
        <Submit busy={busy} testid="signup-submit-button">Create account</Submit>
      </form>
      <p className="mt-6 text-sm text-slate-600">Already have an account? <Link data-testid="goto-login-link" to="/login" className="font-semibold text-emerald-700 hover:underline">Sign in</Link></p>
    </Frame>
  );
}
